import { useId, useState } from 'react'
import { toast } from 'sonner'
import { authApi } from '~/entities/session/api/auth'
import { useAuth } from '~/entities/session/model/use-auth'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/ui/dialog'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'

interface ChangePasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCodeSent?: (email: string) => void
}

export function ChangePasswordDialog({
  open,
  onOpenChange,
  onCodeSent,
}: ChangePasswordDialogProps) {
  const { user } = useAuth()
  const emailId = useId()
  const [email, setEmail] = useState(user?.email || '')
  const [isLoading, setIsLoading] = useState(false)

  const handleRequestReset = async () => {
    if (!email) {
      toast.error(m.pleaseEnterYourEmail())
      return
    }

    setIsLoading(true)
    try {
      await authApi.sendPasswordResetCode(email)
      onOpenChange(false)
      if (onCodeSent) {
        onCodeSent(email)
      } else {
        toast.success(m.passwordResetEmailSent({ email }))
      }
    } catch (error) {
      console.error('Failed to send password reset email:', error)
      toast.error(getErrorMessageFromError(error))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{m.changePassword()}</DialogTitle>
          <DialogDescription>
            {m.enterYourEmailToReceivePasswordResetInstructions()}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor={emailId}>{m.email()}</Label>
            <Input
              id={emailId}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={m.businessexamplecom()}
              disabled={isLoading}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {m.cancel()}
          </Button>
          <Button onClick={handleRequestReset} disabled={isLoading}>
            {isLoading ? m.sending() : m.sendResetEmail()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
